import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  InputBase,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Add, Edit, Delete, Search, Close } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/axios';
import { useDebounce } from '../hooks/useDebounce';

interface Promotion {
  id: string;
  pharmacyProductId: string;
  productName: string;
  description: string;
  backgroundColor: string;
  specialtyIds: string[];
  specialtyNames: string[];
  isActive: boolean;
  startDate: string;
  endDate: string;
  createdAt: string;
}

interface Specialty {
  id: string;
  name: string;
}

interface ProductSearchResult {
  id: string;
  name: string;
  chainName: string;
}

const COLOR_PRESETS: string[] = ['#1a4d8f', '#0d6655', '#5b2c6f', '#1b5e20', '#7b1a1a', '#bf360c'];

// Dominican Republic is UTC-4 (no DST). Shift the UTC instant back 4h and take the
// date portion so a stored "DR end-of-day" UTC timestamp round-trips to the same
// YYYY-MM-DD the admin picked.
const toDRDateOnly = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return new Date(d.getTime() - 4 * 60 * 60 * 1000).toISOString().slice(0, 10);
};

const getStatusInfo = (promo: Promotion) => {
  if (!promo.isActive) return { label: 'INACTIVO', bg: '#E0E0E0', color: '#616161' };
  const now = new Date();
  const start = new Date(promo.startDate);
  const end = new Date(promo.endDate);
  if (now < start) return { label: 'PROGRAMADO', bg: '#E3F2FD', color: '#1565C0' };
  if (now > end) return { label: 'EXPIRADO', bg: '#FFEBEE', color: '#C62828' };
  return { label: 'ACTIVO', bg: '#E8F5E9', color: '#2E7D32' };
};

export const PromotionList: React.FC = () => {
  const queryClient = useQueryClient();
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState<Promotion | null>(null);

  // Form state
  const [pharmacyProductId, setPharmacyProductId] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [description, setDescription] = useState('');
  const [bgColor, setBgColor] = useState<string>(COLOR_PRESETS[0] as string);
  const [specialtyIds, setSpecialtyIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const debouncedProductSearch = useDebounce(productSearch, 300);

  // Queries
  const { data: promotions = [], isLoading } = useQuery<Promotion[]>({
    queryKey: ['promotions'],
    queryFn: async () => {
      const res = await apiClient.get('/promotions');
      return res.data;
    },
  });

  const { data: specialties = [] } = useQuery<Specialty[]>({
    queryKey: ['specialties'],
    queryFn: async () => {
      const res = await apiClient.get('/specialties');
      return res.data;
    },
  });

  const { data: productResults = [], isFetching: isSearching } = useQuery<ProductSearchResult[]>({
    queryKey: ['admin-product-search-promo', debouncedProductSearch],
    queryFn: async () => {
      const res = await apiClient.get(`/pharmacy-products/admin/search?q=${encodeURIComponent(debouncedProductSearch)}`);
      return res.data.results;
    },
    enabled: debouncedProductSearch.length >= 2,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/promotions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`/promotions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/promotions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      setDeleteDialogOpen(false);
      setSelectedPromo(null);
    },
  });

  // Dialog functions
  const openCreateDialog = () => {
    setPharmacyProductId('');
    setSelectedProductName('');
    setDescription('');
    setBgColor(COLOR_PRESETS[0] as string);
    setSpecialtyIds([]);
    setStartDate(new Date().toLocaleDateString('en-CA'));
    setEndDate('');
    setIsActive(true);
    setProductSearch('');
    setEditingPromo(null);
    setDialogOpen(true);
  };

  const openEditDialog = (promo: Promotion) => {
    setPharmacyProductId(promo.pharmacyProductId);
    setSelectedProductName(promo.productName);
    setDescription(promo.description);
    setBgColor(promo.backgroundColor);
    setSpecialtyIds(promo.specialtyIds || []);
    setStartDate(toDRDateOnly(promo.startDate));
    setEndDate(toDRDateOnly(promo.endDate));
    setIsActive(promo.isActive);
    setProductSearch('');
    setEditingPromo(promo);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPromo(null);
  };

  const handleSubmit = () => {
    const payload = {
      pharmacyProductId,
      description: description.trim(),
      backgroundColor: bgColor,
      specialtyIds,
      startDate,
      endDate,
      isActive,
    };

    if (editingPromo) {
      updateMutation.mutate({ id: editingPromo.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const selectProduct = (product: ProductSearchResult) => {
    setPharmacyProductId(product.id);
    setSelectedProductName(product.name);
    setProductSearch('');
  };

  const canSubmit = pharmacyProductId && description.trim() && startDate && endDate;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });

  return (
    <Box>
      {/* Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0D1B35 0%, #1a3a6b 100%)',
          color: 'white',
          py: 4,
          px: { xs: 2, md: 4 },
          mb: 3,
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h2">
            Promociones
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
            Gestiona las promociones visibles para los doctores
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateDialog}
          sx={{
            bgcolor: 'white',
            color: '#0D1B35',
            fontWeight: 600,
            '&:hover': { bgcolor: '#f0f0f0' },
          }}
        >
          Nueva Promoción
        </Button>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Especialidad</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fechas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {promotions.map((promo) => {
                const status = getStatusInfo(promo);
                return (
                  <TableRow key={promo.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: promo.backgroundColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {promo.productName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {promo.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {promo.specialtyNames.length > 0 ? promo.specialtyNames.join(', ') : 'Todos'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(promo.startDate)} — {formatDate(promo.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{
                          bgcolor: status.bg,
                          color: status.color,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEditDialog(promo)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedPromo(promo);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {promotions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No hay promociones creadas</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {/* Product Search */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Producto
            </Typography>
            {selectedProductName ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  bgcolor: '#f9f9f9',
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {selectedProductName}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setPharmacyProductId('');
                    setSelectedProductName('');
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Search sx={{ color: 'text.secondary', mr: 1 }} fontSize="small" />
                  <InputBase
                    placeholder="Buscar producto..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    fullWidth
                    sx={{ fontSize: '0.875rem' }}
                  />
                  {isSearching && <CircularProgress size={18} />}
                </Box>
                {productResults.length > 0 && productSearch && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 10,
                      maxHeight: 200,
                      overflow: 'auto',
                      mt: 0.5,
                    }}
                    elevation={3}
                  >
                    {productResults.slice(0, 5).map((product) => (
                      <Box
                        key={product.id}
                        sx={{
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          borderBottom: '1px solid #f0f0f0',
                        }}
                        onClick={() => selectProduct(product)}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.chainName}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            )}
          </Box>

          {/* Description */}
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          {/* Color Picker */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Color de fondo
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {COLOR_PRESETS.map((color) => (
                <Box
                  key={color}
                  onClick={() => setBgColor(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: color,
                    cursor: 'pointer',
                    border: bgColor === color ? '2px solid #0066CC' : '2px solid transparent',
                    transition: 'border-color 0.2s',
                  }}
                />
              ))}
              {/* Custom color picker */}
              {(() => {
                const isPreset = COLOR_PRESETS.includes(bgColor);
                return (
              <Box
                onClick={() => colorInputRef.current?.click()}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: isPreset ? '#e0e0e0' : bgColor,
                  cursor: 'pointer',
                  border: !isPreset ? '2px solid #0066CC' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Add sx={{ fontSize: 16, color: isPreset ? '#999' : 'white' }} />
                <input
                  ref={colorInputRef}
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </Box>
                );
              })()}
            </Box>
          </Box>

          {/* Specialties — searchable multi-pick */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Especialidades visibles
                {specialtyIds.length > 0 && (
                  <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 400 }}>
                    · {specialtyIds.length} de {specialties.length}
                  </Typography>
                )}
              </Typography>
              {specialtyIds.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setSpecialtyIds([])}
                  sx={{
                    fontSize: 11,
                    textTransform: 'none',
                    color: 'text.secondary',
                    p: 0,
                    minWidth: 0,
                    minHeight: 0,
                    '&:hover': { bgcolor: 'transparent', color: '#0066CC' },
                  }}
                >
                  Limpiar
                </Button>
              )}
            </Box>

            <Autocomplete<Specialty, true, false, false>
              multiple
              options={specialties}
              value={specialties.filter((s) => specialtyIds.includes(s.id))}
              onChange={(_, next) => setSpecialtyIds(next.map((s) => s.id))}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              disableCloseOnSelect
              filterSelectedOptions
              openOnFocus
              autoHighlight
              clearOnBlur={false}
              noOptionsText="Sin resultados"
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const { key: tagKey, ...tagRest } = tagProps as typeof tagProps & { key?: React.Key };
                  return (
                    <Chip
                      {...tagRest}
                      key={option.id}
                      label={option.name}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        fontSize: 12.5,
                        height: 26,
                        bgcolor: 'rgba(0,102,204,0.08)',
                        color: '#0052a3',
                        border: '1px solid rgba(0,102,204,0.2)',
                        borderRadius: 1,
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(0,102,204,0.55)',
                          fontSize: 16,
                          '&:hover': { color: '#0052a3' },
                        },
                      }}
                    />
                  );
                })
              }
              renderOption={(props, option) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
                return (
                  <li
                    {...rest}
                    key={option.id}
                    style={{
                      fontSize: '0.9rem',
                      padding: '8px 14px',
                      fontFamily: 'inherit',
                    }}
                  >
                    {option.name}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={
                    specialtyIds.length === 0
                      ? 'Buscar especialidad — vacío = visible a todas'
                      : 'Añadir otra…'
                  }
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 8px 24px -8px rgba(13,27,53,0.15)',
                  },
                },
              }}
              ListboxProps={{
                sx: {
                  maxHeight: 240,
                  '& .MuiAutocomplete-option': {
                    py: 1,
                    '&.Mui-focused': { bgcolor: 'rgba(0,102,204,0.06)' },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  alignItems: 'flex-start',
                  gap: 0.5,
                  py: specialtyIds.length > 0 ? 0.75 : 0.25,
                  minHeight: 42,
                },
              }}
            />

            {specialtyIds.length === 0 && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontStyle: 'italic' }}
              >
                Sin selección — la promoción será visible a doctores de todas las especialidades.
              </Typography>
            )}
          </Box>

          {/* Dates */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Fecha inicio"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Fecha fin"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Active Toggle */}
          <FormControlLabel
            control={
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            }
            label="Activa"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || createMutation.isPending || updateMutation.isPending}
            sx={{ bgcolor: '#0D1B35', '&:hover': { bgcolor: '#1a3a6b' } }}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : editingPromo ? (
              'Guardar'
            ) : (
              'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Eliminar Promoción</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar la promoción de{' '}
            <strong>{selectedPromo?.productName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedPromo && deleteMutation.mutate(selectedPromo.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
